import { put, takeLatest, select, call } from 'typed-redux-saga/macro';
import { roomsActionTypes, TRoom, TSubscribeToRoomAction } from '../../types/rooms';
import { memberApi } from '../../lib/api';
import { RoomMessageModel } from '../../lib/db';
import TStore from '../../types/store';
import { TMessage } from '../../types/rooms';
import { NS_MUC, parseSubscribedRooms } from '../../helpers/strophe-helpers';
import hasDisabledSubmitToRoom from '../../helpers/has-disabled-submit-to-room';
import connectionService from '@services/internal/connection.service';

function* joinXmppRoom(roomId: string, since: string) {
  const connection = yield* call(connectionService.waitForConnection);
  const nickname = yield* select((store: TStore) => store.users.currentUser?.jabberId);

  if (since) {
    connection.send(
      $pres({ to: `${roomId}/${nickname}` })
        .c('x', { xmlns: NS_MUC })
        .c('history', { since }),
    );
  } else {
    connection.send($pres({ to: `${roomId}/${nickname}` }).c('x', { xmlns: NS_MUC }));
  }
}

function* getRoomsRequest() {
  try {
    const currentLang = yield* select((store: TStore) => store.app.currentLang);
    const request: any = yield* call(memberApi.getRooms, { isActive: true, lang: currentLang });
    const nickname = yield* select((store: TStore) => store.users.currentUser?.jabberId);
    const connection = yield* call(connectionService.waitForConnection);
    /**
     * Get subscribed rooms IQ
     */
    const iq = $iq({
      from: nickname as string,
      to: process.env.REACT_APP_XMPP_MUC_SERVER_URI as string,
      type: 'get',
      id: connection.getUniqueId(),
    }).c('subscriptions', { xmlns: 'urn:xmpp:mucsub:0' });

    const subscribedRooms: string[] = yield new Promise<string[]>((res, reject) => {
      connection.sendIQ(
        iq,
        (iqs: Element) => {
          res(parseSubscribedRooms(Strophe.serialize(iqs)) as string[]);
        },
        reject,
      );
    });

    const responseSorted: TRoom[] = request.data.data.sort(
      (a: TRoom, b: TRoom) => a.order - b.order,
    );

    yield put({
      type: roomsActionTypes.GET_ROOMS_SUCCESS,
      payload: responseSorted.map((room) => {
        room.isMember = subscribedRooms.includes(room.id);
        return room;
      }),
    });
  } catch (e: any) {
    console.log('getRoomsRequest/e:', e);
  }
}

function* joinRoomRequest({ payload }: any) {
  yield put({
    type: roomsActionTypes.SET_ROOMS_LOADING,
    payload: true,
  });

  try {
    const messages: RoomMessageModel[] = yield RoomMessageModel.getMessagesByRoomId(payload);
    const lastStamp: string = yield RoomMessageModel.getLastMessageStampByRoomId(payload);
    const currentRoom = Strophe.getBareJidFromJid(payload);
    const doctorExpertises = yield* select((store: TStore) => store.users.currentUser?.expertise);

    yield put({
      type: roomsActionTypes.JOIN_ROOM_SUCCESS,
      payload: {
        currentRoom,
        messages,
        disabledSubmit: hasDisabledSubmitToRoom(currentRoom, doctorExpertises || []),
      },
    });
    yield joinXmppRoom(payload, lastStamp);
  } catch {}

  yield put({
    type: roomsActionTypes.SET_ROOMS_LOADING,
    payload: false,
  });
}

function* leaveRoomRequest() {
  try {
    const roomId = yield* select((store: TStore) => store.rooms.currentRoom);
    const connection = yield* call(connectionService.waitForConnection);
    const nickname = yield* select((store: TStore) => store.users.currentUser?.jabberId);
    yield connection.send($pres({ to: `${roomId}/${nickname}`, type: 'unavailable' }));
  } catch {
  } finally {
    yield put({ type: roomsActionTypes.LEAVE_ROOM_SUCCESS });
  }
}

function* addMessageRequest({ payload }: any) {
  try {
    const roomId = yield* select((store: TStore) => store.rooms.currentRoom);
    const messages = yield* select((store: TStore) => store.rooms.messages);

    yield put({
      type: roomsActionTypes.ADD_MESSAGE_SUCCESS,
      payload: [...messages, payload],
    });

    if (roomId) {
      const message: TMessage = payload;
      const messageModel = new RoomMessageModel({
        roomId,
        ...message,
      });
      yield messageModel.save();
    }
  } catch {}
}

export function* unsubscribeFromRoom(action: any) {
  try {
    const connection = yield* call(connectionService.waitForConnection);
    const jabberId = yield* select((store: TStore) => store.users.currentUser?.jabberId);

    const iq = $iq({
      from: jabberId as string,
      to: action.payload,
      type: 'set',
      id: connection.getUniqueId(),
    }).c('unsubscribe', { xmlns: 'urn:xmpp:mucsub:0' });

    yield new Promise<void>((res, rej) => {
      connection.sendIQ(
        iq,
        (iqs: Element) => {
          res();
        },
        (e: any) => rej(e),
      );
    });

    connection.send($pres({ to: action.payload, type: 'unavailable' }));

    yield* call(getRoomsRequest);
  } catch (err) {
    console.log('XMPP subscribeToRoom/err:', err);
  }
}

/**
 * Subscribe to MucSub events.
 * Messages, presence, subscribers
 */
export function* subscribeToRoom(action: TSubscribeToRoomAction) {
  try {
    const connection = yield* call(connectionService.waitForConnection);
    const jabberId = yield* select((store: TStore) => store.users.currentUser?.jabberId);
    const id = connection.getUniqueId();

    const iq = $iq({
      from: jabberId as string,
      to: action.payload.roomId,
      type: 'set',
      id,
    }).c('subscribe', { xmlns: 'urn:xmpp:mucsub:0', nick: jabberId as string });

    yield new Promise<void>((res, rej) => {
      connection.sendIQ(
        iq,
        (iqs: Element) => {
          res();
        },
        (e: any) => {
          rej(e);
        },
      );
    });
    yield* call(getRoomsRequest);
  } catch (err) {
    console.log('XMPP subscribeToRoom/err:', err);
  }
}

function* roomsSaga() {
  yield takeLatest(roomsActionTypes.GET_ROOMS_REQUEST, getRoomsRequest);
  yield takeLatest(roomsActionTypes.JOIN_ROOM_REQUEST, joinRoomRequest);
  yield takeLatest(roomsActionTypes.ADD_MESSAGE_REQUEST, addMessageRequest);
  yield takeLatest(roomsActionTypes.LEAVE_ROOM_REQUEST, leaveRoomRequest);
  yield takeLatest(roomsActionTypes.UNSUBSCRIBE_ROOM, unsubscribeFromRoom);
  yield takeLatest(roomsActionTypes.SUBSCRIBE_TO_ROOM, subscribeToRoom);
}

export default roomsSaga;
